<template>
  <section ref="containerRef" class="honor-container">
    <!-- Elementos decorativos (Izquierda) -->
    <div ref="decoIzqRef" class="deco-side deco-izq">
      <img :src="bolaPng" alt="Bola de disco" class="disco-ball" />
      <img :src="petalosPng" alt="Pétalos" class="petalos-img" />
    </div>

    <!-- Elementos decorativos (Derecha) -->
    <div ref="decoDerRef" class="deco-side deco-der">
      <img :src="bolaPng" alt="Bola de disco" class="disco-ball" />
      <img :src="petalosPng" alt="Pétalos" class="petalos-img" />
    </div>

    <!-- Título -->
    <header class="honor-header">
      <h2 class="title-serif">Cuadro de Honor</h2>
      <div class="title-line"></div>
      <br />
    </header>

    <!-- Quinceañera -->
    <article ref="quinceaneraRef" class="honor-hero">
      <h3 class="hero-label">Quinceañera</h3>
      <div class="title-line"></div>
      <p class="hero-name">Sara Paulina</p>
    </article>

    <!-- Chambelán principal -->
    <article ref="honorRef" class="honor-hero">
      <h3 class="hero-label">Chambelán Principal</h3>
      <div class="title-line"></div>
      <p class="hero-name">Karol López</p>
    </article>

    <br />

    <!-- Chambelanes -->
    <section class="honor-section">
      <h3 class="section-title">Chambelanes</h3>
      <div class="title-line"></div>

      <div class="honor-grid">
        <div
          v-for="(n, i) in chambelanes"
          :key="'cham-' + i"
          :ref="(el) => setChamRef(el, i)"
          class="honor-item"
        >
          <span class="honor-chip">{{ n }}</span>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue'
import gsap from 'gsap'
import petalosPng from '@/assets/petalos.png'
import bolaPng from '@/assets/bola.avif'

export default {
  name: 'CuadroDeHonor',
  setup() {
    // Datos
    const chambelanes = [
      'Eder López',
      'Jadiel Torres',
      'Elihú Martínez',
      'Tadeo Fuentes'
    ]

    // Refs
    const containerRef = ref(null)
    const quinceaneraRef = ref(null)
    const honorRef = ref(null)
    const chamRefs = ref([])
    const decoIzqRef = ref(null)
    const decoDerRef = ref(null)

    const setChamRef = (el, i) => (chamRefs.value[i] = el)

    // Animaciones de héroes (Quinceañera / Chambelán)
    const animateHero = (targetRef, delayOffset = 0) => {
      if (!targetRef.value) return
      gsap.fromTo(
        targetRef.value,
        { y: -24, opacity: 0, filter: 'blur(6px)' },
        {
          y: 0,
          opacity: 1,
          filter: 'blur(0px)',
          duration: 0.55,
          ease: 'power3.out',
          delay: delayOffset
        }
      )
      const label = targetRef.value.querySelector('.hero-label')
      const name = targetRef.value.querySelector('.hero-name')
      gsap.fromTo(
        label,
        { y: 14, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.45, ease: 'power2.out', delay: delayOffset + 0.1 }
      )
      gsap.fromTo(
        name,
        { y: 14, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.45, ease: 'power2.out', delay: delayOffset + 0.18 }
      )
    }

    const createObserver = (elements, baseDelay = 0.04) => {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return
            const idx = elements.indexOf(entry.target)
            const direction = idx % 3 === 0 ? -40 : idx % 3 === 1 ? 40 : 0
            const yStart = idx % 3 === 2 ? 26 : 0
            gsap.fromTo(
              entry.target,
              { x: direction, y: yStart, opacity: 0, scale: 0.96, filter: 'blur(4px)' },
              {
                x: 0,
                y: 0,
                opacity: 1,
                scale: 1,
                filter: 'blur(0px)',
                duration: 0.5,
                delay: idx * baseDelay,
                ease: 'power3.out'
              }
            )
            io.unobserve(entry.target)
          })
        },
        { threshold: 0.25 }
      )
      elements.forEach((el) => el && io.observe(el))
    }

    // Animación de entrada única para pétalos y rotación continua para bolas de disco
    const animateDecorations = () => {
      // Pétalos: animación de entrada únicamente
      if (decoIzqRef.value) {
        const petaloIzq = decoIzqRef.value.querySelector('.petalos-img')
        gsap.fromTo(
          petaloIzq,
          { x: -35, opacity: 0, scale: 0.9, filter: 'blur(4px)' },
          { x: 0, opacity: 0.85, scale: 1, filter: 'blur(0px)', duration: 0.9, ease: 'power2.out' }
        )
      }
      if (decoDerRef.value) {
        const petaloDer = decoDerRef.value.querySelector('.petalos-img')
        gsap.fromTo(
          petaloDer,
          { x: 35, opacity: 0, scale: 0.9, filter: 'blur(4px)' },
          { x: 0, opacity: 0.85, scale: 1, filter: 'blur(0px)', duration: 0.9, ease: 'power2.out' }
        )
      }

      // Bola de disco: rotación continua, fina y sutil
      const bolas = document.querySelectorAll('.disco-ball')
      if (bolas.length) {
        gsap.fromTo(
          bolas,
          { opacity: 0, scale: 0.8 },
          { opacity: 0.9, scale: 1, duration: 0.6, ease: 'power2.out' }
        )
        gsap.to(bolas, {
          rotation: 360,
          duration: 18,
          repeat: -1,
          ease: 'none'
        })
      }
    }

    onMounted(async () => {
      await nextTick()
      animateHero(quinceaneraRef, 0)
      animateHero(honorRef, 0.15)
      createObserver(chamRefs.value, 0.05)
      animateDecorations()
    })

    return {
      chambelanes,
      quinceaneraRef,
      honorRef,
      containerRef,
      decoIzqRef,
      decoDerRef,
      setChamRef,
      petalosPng,
      bolaPng
    }
  }
}
</script>

<style scoped>
/* Contenedor general – rosa elegante */
.honor-container {
  position: relative;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 42px 18px 56px;
  background: #f5b9c9;
  background-image: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.06),
      rgba(255, 255, 255, 0)
    ),
    radial-gradient(
      1000px 400px at 50% -10%,
      rgba(255, 255, 255, 0.32),
      rgba(255, 255, 255, 0) 60%
    ),
    linear-gradient(180deg, #f5b9c9, #f5b9c9);
  border-radius: 18px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

/* Decoraciones en las orillas */
.deco-side {
  position: absolute;
  top: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  pointer-events: none;
  z-index: 10;
}

.deco-izq {
  top:700px;
  left: -40px;
}

.deco-der {
    top:50px;
z-index: 999;
  right: -50px;
}

.disco-ball {
  width: 120px;
  height: 120px;
  object-fit: contain;
  filter: drop-shadow(0 2px 6px rgba(255, 255, 255, 0.4));
}

.petalos-img {
  width: 0px;
  height: auto;
  object-fit: contain;
  opacity: 0.85;
}

.deco-der .petalos-img {
  transform: scaleX(-1); /* Invertir pétalos a la derecha */
}

/* Header */
.honor-header {
  position: relative;
  z-index: 2;
  text-align: center;
  margin-bottom: 18px;
}
.title-serif {
  font-family: 'quote', serif;
  font-size: 36px;
  color: #fff;
  line-height: 1.05;
  margin: 0 0 8px;
}
.title-line {
  width: 64px;
  height: 2px;
  margin: 0 auto;
  background: #fff;
  opacity: 0.85;
  border-radius: 2px;
}

/* Tarjeta destacada */
.honor-hero {
  position: relative;
  z-index: 2;
  text-align: center;
  border-radius: 16px;
  padding: 18px 16px;
  margin: 18px auto 24px;
  max-width: 680px;
}
.hero-label {
  font-family: 'quote', serif;
  font-size: 27px;
  color: #ffffff;
  font-weight: 700;
  letter-spacing: 0.16em;
}
.hero-name {
  font-family: 'quote', cursive;
  font-size: 24px;
  color: #ffffff;
  margin: 8px 0 0;
}

/* Secciones */
.honor-section {
  position: relative;
  z-index: 2;
  margin-top: 8px;
}
.section-title {
  text-align: center;
  font-family: 'quote', serif;
  font-weight: 700;
  letter-spacing: 0.16em;
  font-size: 27px;
  color: #ffffff;
  margin: 10px 0 12px;
}

/* Grid de nombres */
.honor-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
  margin-top: 16px;
}
.honor-item {
  opacity: 0; /* Animado con GSAP */
}
.honor-chip {
  display: block;
  width: 100%;
  padding: 12px 14px;
  color: #ffffff;
  border-radius: 14px;
  font-family: 'quote', serif;
  font-size: 24px !important;
  text-align: center;
}

/* Hover sutil */
@media (hover: hover) {
  .honor-chip:hover {
    transform: translateY(-1px);
    transition: transform 0.18s ease, box-shadow 0.18s ease;
    box-shadow: 0 10px 18px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.75);
  }
}

</style>